'use client';

import React, { useState, useEffect, useCallback } from 'react';
import type { Client, Service } from '@/core/domain';
import { SERVICE_TYPE_LABELS } from '@/core/domain';
import { Button } from '@/core/presentation/components/ui/Button';
import { Spinner } from '@/core/presentation/components/ui/Spinner';
import AppIcon from '@/core/presentation/components/ui/AppIcon';
import { SectionScreenLayout } from '@/features/app/presentation/components/SectionScreenLayout';
import { formatPhone } from '@/core/utils/formatPhone';
import { useClients } from '../context/ClientContext';
import { useAwaitInitialFetch } from '@/features/app/presentation/hooks/use-await-initial-fetch';

function getServiceTypeDisplayName(service: Service): string {
  if (service.type === 'outro' && service.type_other_description?.trim()) {
    return service.type_other_description.trim();
  }
  return SERVICE_TYPE_LABELS[service.type];
}

function formatDate(iso: string): string {
  return new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

import { FORM_FIELD_CONTROL_APP, FORM_FIELD_LABEL_APP } from '@/core/presentation/constants/form-field-ui.constants';

const inputClasses = FORM_FIELD_CONTROL_APP;
const labelClasses = FORM_FIELD_LABEL_APP;

export interface PreselectedClientForService {
  id: string;
  nome: string;
  whatsapp: string;
  endereco?: string;
}

export interface ClientsSectionProps {
  /** Ao clicar em "Registrar novo serviço" no histórico, navegar para a seção; recebe o cliente para pré-selecionar */
  onRegisterServiceClick?: (client?: PreselectedClientForService) => void;
}

/** Formata telefone para exibição (aceita número com ou sem código do país). */
function displayPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  const national = digits.length > 10 ? digits.replace(/^55/, '') : digits;
  return formatPhone(national);
}

export const ClientsSection: React.FC<ClientsSectionProps> = ({
  onRegisterServiceClick,
}) => {
  const { state, events } = useClients();
  const loadClients = useCallback(async () => {
    await events.getByUserId();
  }, [events.getByUserId]);
  const clientsDataReady = useAwaitInitialFetch(loadClients, [loadClients]);
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  /** Dados exibidos do cliente no histórico (editáveis ao clicar em Editar) */
  const [clientData, setClientData] = useState<{ nome: string; whatsapp: string; endereco: string }>({
    nome: '',
    whatsapp: '',
    endereco: '',
  });
  const [editingData, setEditingData] = useState(false);
  const [editNome, setEditNome] = useState('');
  const [editWhatsapp, setEditWhatsapp] = useState('');
  const [editEndereco, setEditEndereco] = useState('');

  useEffect(() => {
    if (selectedClient) {
      setClientData({
        nome: selectedClient.name,
        whatsapp: selectedClient.phone,
        endereco: selectedClient.address ?? '',
      });
      setEditingData(false);
    }
  }, [selectedClient]);

  const handleOpenClient = (client: Client) => {
    setSelectedClient(client);
    setView('detail');
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedClient(null);
  };

  const selectedCws = selectedClient
    ? state.clientsWithServices.find((cws) => cws.client.id === selectedClient.id)
    : null;
  const servicosDoCliente = selectedCws?.services ?? [];
  const proximaManutencao = selectedCws?.nextMaintenanceDate ?? null;

  if (view === 'detail' && selectedClient) {
    return (
      <SectionScreenLayout
        title="Histórico do cliente"
        subtitle={clientData.nome}
      >
        <div className="space-y-6">
          <button
            type="button"
            onClick={handleBackToList}
            className="flex items-center gap-2 text-sm text-cyan hover:text-cyan-dark font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan rounded"
          >
            <AppIcon name="ArrowLeftIcon" size={18} />
            Voltar à lista
          </button>

          <section className="rounded-xl border border-border-light bg-white p-4 sm:p-5 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-navy-dark uppercase tracking-wide">
                Dados do cliente
              </h2>
              {editingData ? (
                <button
                  type="button"
                  onClick={() => {
                    setEditNome(clientData.nome);
                    setEditWhatsapp(clientData.whatsapp);
                    setEditEndereco(clientData.endereco);
                    setEditingData(false);
                  }}
                  className="shrink-0 p-2 rounded-lg text-text-muted hover:text-red-600 hover:bg-red-50 border border-border-light hover:border-red-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan"
                  aria-label="Descartar alterações"
                >
                  <AppIcon name="XMarkIcon" size={20} />
                </button>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 shrink-0 !text-cyan !bg-cyan/15 font-medium hover:!bg-cyan/25 hover:!text-cyan-dark"
                  onClick={() => {
                    setEditNome(clientData.nome);
                    setEditWhatsapp(clientData.whatsapp);
                    setEditEndereco(clientData.endereco);
                    setEditingData(true);
                  }}
                >
                  <AppIcon name="PencilSquareIcon" size={16} />
                  Editar
                </Button>
              )}
            </div>
            {editingData ? (
              <div className="space-y-4 pt-1">
                <div>
                  <label htmlFor="cliente-edit-nome" className={labelClasses}>
                    Nome
                  </label>
                  <input
                    id="cliente-edit-nome"
                    type="text"
                    value={editNome}
                    onChange={(e) => setEditNome(e.target.value)}
                    placeholder="Nome do cliente"
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label htmlFor="cliente-edit-whatsapp" className={labelClasses}>
                    WhatsApp
                  </label>
                  <input
                    id="cliente-edit-whatsapp"
                    type="tel"
                    value={editWhatsapp}
                    onChange={(e) => setEditWhatsapp(formatPhone(e.target.value))}
                    placeholder="(00) 00000-0000"
                    inputMode="tel"
                    autoComplete="tel"
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label htmlFor="cliente-edit-endereco" className={labelClasses}>
                    Endereço <span className="text-text-muted text-xs font-normal">(opcional)</span>
                  </label>
                  <input
                    id="cliente-edit-endereco"
                    type="text"
                    value={editEndereco}
                    onChange={(e) => setEditEndereco(e.target.value)}
                    placeholder="Endereço"
                    className={inputClasses}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => {
                      setEditNome(clientData.nome);
                      setEditWhatsapp(clientData.whatsapp);
                      setEditEndereco(clientData.endereco);
                      setEditingData(false);
                    }}
                  >
                    <AppIcon name="XMarkIcon" size={16} />
                    Descartar
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => {
                      setClientData({
                        nome: editNome.trim(),
                        whatsapp: editWhatsapp,
                        endereco: editEndereco.trim(),
                      });
                      setEditingData(false);
                    }}
                  >
                    <AppIcon name="CheckIcon" size={16} />
                    Salvar
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <p className="font-medium text-navy-dark">{clientData.nome}</p>
                <p className="text-sm text-text-muted">{displayPhone(clientData.whatsapp)}</p>
                {clientData.endereco ? (
                  <p className="text-sm text-text-muted">{clientData.endereco}</p>
                ) : null}
              </>
            )}
          </section>

          <section className="rounded-xl border border-border-light bg-white p-4 sm:p-5 space-y-3">
            <h2 className="text-sm font-semibold text-navy-dark uppercase tracking-wide">
              Próxima manutenção
            </h2>
            <p className="text-lg font-semibold text-cyan-dark tabular-nums">
              {proximaManutencao ? formatDate(proximaManutencao) : '—'}
            </p>
          </section>

          <section className="rounded-xl border border-border-light bg-white p-4 sm:p-5 space-y-3">
            <h2 className="text-sm font-semibold text-navy-dark uppercase tracking-wide">
              Serviços realizados
            </h2>
            {servicosDoCliente.length > 0 ? (
              <ul className="divide-y divide-border-light">
                {servicosDoCliente.map((s) => (
                  <li key={s.id} className="py-3 flex justify-between items-center gap-2">
                    <span className="font-medium text-navy-dark">{getServiceTypeDisplayName(s)}</span>
                    <span className="text-sm text-text-muted tabular-nums">
                      {formatDate(s.service_date)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-text-muted">Nenhum serviço registrado.</p>
            )}
          </section>

          <div className="flex justify-end pt-2">
            <Button
              type="button"
              size="lg"
              className="gap-2"
              onClick={() => selectedClient && onRegisterServiceClick?.({ id: selectedClient.id, nome: clientData.nome, whatsapp: clientData.whatsapp, endereco: clientData.endereco || undefined })}
            >
              <AppIcon name="PlusCircleIcon" size={20} />
              Registrar novo serviço
            </Button>
          </div>
        </div>
      </SectionScreenLayout>
    );
  }

  const clientsWithServices = state.clientsWithServices;

  return (
    <SectionScreenLayout
      title="Clientes"
      subtitle="Lista com último serviço e próxima manutenção. Toque em um cliente para ver o histórico."
    >
      <div className="space-y-4">
        {state.error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {state.error}
          </div>
        )}
        {!clientsDataReady ? (
          <div className="flex items-center gap-2 text-text-muted text-sm py-2">
            <Spinner size="sm" className="text-cyan" aria-hidden />
            <span>Carregando</span>
          </div>
        ) : clientsWithServices.length === 0 ? (
          <div className="rounded-xl border border-border-light bg-[#fafbfc] p-8 text-center">
            <p className="text-text-muted text-sm">
              Nenhum cliente cadastrado. Registre um serviço para começar.
            </p>
          </div>
        ) : (
          <>
            <h2 className="text-sm font-semibold text-navy-dark uppercase tracking-wide flex items-center gap-2">
              <AppIcon name="UserGroupIcon" size={18} className="text-cyan shrink-0" />
              Lista de clientes
            </h2>
            <ul className="space-y-2">
              {clientsWithServices.map((cws) => {
                const lastService = cws.services[0];
                return (
                  <li key={cws.client.id}>
                    <button
                      type="button"
                      onClick={() => handleOpenClient(cws.client)}
                      className="w-full text-left rounded-xl border border-border-light bg-white p-4 sm:p-5 hover:border-cyan/40 hover:bg-cyan/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2 flex items-center gap-3"
                    >
                      <AppIcon name="UserCircleIcon" size={28} className="text-cyan/70 shrink-0" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-semibold text-navy-dark">{cws.client.name}</span>
                          <span className="text-xs text-text-muted tabular-nums">
                            Próxima: {cws.nextMaintenanceDate ? formatDate(cws.nextMaintenanceDate) : '—'}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-text-muted">
                          {lastService
                            ? `${getServiceTypeDisplayName(lastService)} — ${formatDate(lastService.service_date)}`
                            : displayPhone(cws.client.phone)}
                        </p>
                      </div>
                      <AppIcon name="ChevronRightIcon" size={20} className="text-text-muted shrink-0" aria-hidden />
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </SectionScreenLayout>
  );
};
